import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Col, Row } from "reactstrap";
import { get, uniqueId } from "lodash";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  Collapse,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Button,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";

import { getOptionLabel, getOptionValue } from "common/enums";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import Tooltip from "Components/Common/Tooltip";
import { DocumentName } from "pages/UtilityConsumers/style";
import CommentInput from "Components/Common/CommentInput";
import CommentBox from "Components/Common/CommentBox";
import {
  FIRE_EMERGENCY_CHIEF_FIRE_OFFICER,
  FIRE_SAFETY_CONSUMER,
} from "common/constant";
import AdminDiscussionForBuildingObservation from "common/AdminDiscussionForBuildingObservation";

import { convertCamelTextToCapitalizeText } from "utils/convertCamelTextToCapitalizeText";
import getFileExtension from "utils/getFileExtension";
import { generateFormData, getImageUrl, isAuthorizedUser } from "utils";
import removeExtension from "utils/removeExtension";

import {
  useLazyGetBuildingObservationAdminCommentsByIdQuery,
  useLazyGetBuildingObservationCommentQuery,
  usePostBuildingObservationCommentMutation,
} from "apis/ApplicationRequestComment";
import {
  usePostFireSafetyResolvedObservationPhotographsMutation,
  usePutCloseBuildingObservationByIdMutation,
} from "apis/FireSafety/FireSafetyObservation";

import { UploadImage } from "./style";
import InputFieldText from "Components/Common/InputFieldText";

const validFileFormats = ["jpg", "png", "jpeg"];

const FireSafetyViewBuildingObservationDetails = ({
  observationDetails,
  index,
  buildingInformationId,
  t,
}) => {
  const {
    id: observationId,
    fireSafetyUser: { roles: currentRoles = [] } = {},
    isApproved,
    isResolved,
    planOfAction,
    submitPlanOfAction: submitActionPlan,
    resolvedPhotographs,
  } = observationDetails;

  const [addComment] = usePostBuildingObservationCommentMutation();
  const [addResolvePhotographs, { isLoading: isResolvePhotographLoading }] =
    usePostFireSafetyResolvedObservationPhotographsMutation();

  const [getObservationComment, { data: { data: commentData = [] } = {} }] =
    useLazyGetBuildingObservationCommentQuery();

  const [
    closeBuildingObservation,
    { isLoading: isCloseBuildingObservationLoading },
  ] = usePutCloseBuildingObservationByIdMutation();

  const [
    getAdminObservationComment,
    { data: { data: adminComments = [] } = {} },
  ] = useLazyGetBuildingObservationAdminCommentsByIdQuery(
    buildingInformationId
  );

  const [documentToPreview, setDocumentToPreview] = useState(null);
  const [openDiscussion, setOpenDiscussion] = useState(true);
  const [openObservation, setOpenObservation] = useState(false);
  const [resolveImages, setResolveImages] = useState(resolvedPhotographs ?? []);
  const [submitPlanOfAction, setSubmitPlanOfAction] = useState(
    submitActionPlan ?? ""
  );

  const { roles } = useSelector((state) => state.Login);

  const isAdmin = useMemo(() => !roles.includes(FIRE_SAFETY_CONSUMER), [roles]);

  // Role is same as logged in admin OR role is CFO
  const isAuthorizedAdminRole = useMemo(
    () =>
      isAuthorizedUser(roles, currentRoles) ||
      roles.includes(FIRE_EMERGENCY_CHIEF_FIRE_OFFICER),
    [currentRoles]
  );

  const isPlanOfActionMoreThanSevenDays = useMemo(
    () =>
      planOfAction ===
      getOptionValue("planOfActionEnum", "More Than Seven Days"),
    [planOfAction]
  );

  const fields = useMemo(
    () => [
      {
        name: "Observation Type",
        key: "observationType",
        isRequired: true,
      },
      {
        name: "Tenant Name",
        key: "floorWiseTenant.name",
        isRequired: false,
      },
      {
        name: "Plan Of Action",
        key: "planOfAction",
        renderField: (value) =>
          getOptionLabel("planOfActionEnum", value) || "-",
      },
      {
        name: "Severity",
        key: "severity",
        renderField: (value) => getOptionLabel("severityEnum", value) || "-",
      },
      {
        name: "Urgency",
        key: "urgency",
        renderField: (value) => getOptionLabel("urgencyEnum", value) || "-",
        isVisible:
          planOfAction ===
          getOptionValue("planOfActionEnum", "More Than Seven Days"),
      },
      {
        name: "Submit Plan Of Action",
        key: "submitPlanOfAction",
        isRequired: true,
        isVisible:
          planOfAction ===
          getOptionValue("planOfActionEnum", "More Than Seven Days"),
      },
      {
        name: "Provision",
        key: "provision",
        isRequired: true,
        renderField: (value) =>
          value ? (
            <ul className="ps-3">
              {value
                .split("\r\n")
                .filter(Boolean)
                .map((str, index) => (
                  <li key={index}>{str}</li>
                ))}
            </ul>
          ) : (
            "-"
          ),
      },
      {
        name: "Recommendations",
        key: "recommendations",
        isRequired: true,
        renderField: (value) =>
          value ? (
            <ul className="ps-3">
              {value
                .split("\r\n")
                .filter(Boolean)
                .map((str, index) => (
                  <li key={index}>{str}</li>
                ))}
            </ul>
          ) : (
            "-"
          ),
      },
    ],
    [planOfAction]
  );

  const previewButtonHandler = (documentURL) => {
    const fileExtension = getFileExtension(documentURL ?? "");
    const url = getImageUrl(documentURL ?? "");
    setDocumentToPreview({
      documentTypeName: "",
      url,
      documentType: fileExtension,
      documentName: removeExtension(documentURL ?? ""),
    });
  };

  const addNewComment = async (comment, inspectionObservationId) => {
    try {
      await addComment({
        comment,
        inspectionObservationId,
      });
    } catch (error) {}
  };

  const handleUploadResolvePhotographs = async () => {
    try {
      const payload = {
        resolvedPhotographs: resolveImages.map(
          ({ id, file, photographUrl }) => ({
            id: file ? 0 : id,
            file: file ?? "",
            photographUrl: photographUrl ?? "",
          })
        ),
        ...(isPlanOfActionMoreThanSevenDays && {
          submitPlanOfAction,
        }),
      };

      const formData = new FormData();
      generateFormData(formData, payload);

      await addResolvePhotographs(observationId, formData);
    } catch (error) {
      console.error(error);
    }
  };

  const handleApproveResolvePhotographs = async () => {
    try {
      await closeBuildingObservation(observationId);
    } catch (error) {
      console.error(error);
    }
  };

  const getCommentsByObservationId = (comments, id) =>
    comments.find(
      ({ inspectionObservationId }) => id === inspectionObservationId
    )?.inspectionObservationComments || [];

  const handleDiscussionCollapse = () => {
    setOpenDiscussion(!openDiscussion);
  };

  const handleObservationCollapse = () => {
    setOpenObservation(!openObservation);
  };

  const fileChangeHandler = (files) => {
    const isAllFileValid = files.every(
      (file) =>
        validFileFormats.includes(getFileExtension(file.name.toLowerCase())) &&
        file.size < 10485760
    );

    if (!isAllFileValid) {
      toast.error("Please upload image and size should be less than 10 mb");
    } else {
      setResolveImages((resolveImages) => [
        ...resolveImages,
        ...files.map((file) => ({
          id: uniqueId("doc__"),
          url: URL.createObjectURL(file),
          name: file.name,
          file: file,
        })),
      ]);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        if (buildingInformationId) {
          if (isAdmin) {
            await getAdminObservationComment(buildingInformationId);
          }
          await getObservationComment(buildingInformationId);
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [buildingInformationId]);

  useEffect(() => setResolveImages(resolvedPhotographs), [resolvedPhotographs]);

  return (
    <>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}

      <>
        <div
          className="fs-4 fw-semibold mb-3 border-bottom"
          onClick={handleObservationCollapse}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <label className="fw-semibold h5">
              <p className="mb-1">
                {t("Building Observation")} {index + 1} ( Status :{" "}
                {isResolved ? "Approved" : "Pending"} )
              </p>
              <p className="mb-1">
                {t("Observation Added By")}:
                {convertCamelTextToCapitalizeText(
                  observationDetails.fireSafetyUser.userName
                )}{" "}
                ({observationDetails.fireSafetyUser.roleName} of{" "}
                {observationDetails.fireSafetyUser.departmentName})
              </p>
            </label>
            <p>{openObservation ? <ExpandLess /> : <ExpandMore />}</p>
          </Stack>
        </div>
        <Collapse in={openObservation} timeout="auto" unmountOnExit>
          <Row className="d-flex  mb-2">
            {fields.map(({ name, key, isVisible, isRequired, renderField }) => {
              const value = get(observationDetails, key, "");
              if (typeof isVisible !== "boolean" || isVisible) {
                return (
                  <Col key={key} lg={6} className="mb-1">
                    <Row>
                      <Col md={5}>{t(name)}:</Col>
                      <Col
                        md={7}
                        className="fw-semibold"
                        style={{ wordBreak: "break-word" }}
                      >
                        {renderField
                          ? renderField(value)
                          : get(observationDetails, key, null) ??
                            (isRequired ? "-" : "N/A")}
                      </Col>
                    </Row>
                  </Col>
                );
              }
            })}
            {!!observationDetails.observationPhotographs.length && (
              <Row className="d-flex gap-3 flex-wrap mt-1">
                {observationDetails.observationPhotographs.map(
                  ({ photographUrl, name }, index) => (
                    <Col
                      key={index}
                      lg={2}
                      md={4}
                      sm={6}
                      xs={12}
                      className="image-container"
                    >
                      <a onClick={() => previewButtonHandler(photographUrl)}>
                        <Box
                          component="img"
                          className="image d-block m-auto cursor-pointer"
                          alt="Image"
                          src={getImageUrl(photographUrl)}
                        />
                      </a>
                      <Tooltip title={name}>
                        <DocumentName fontSize={13} variant="body2">
                          {name}
                        </DocumentName>
                      </Tooltip>
                    </Col>
                  )
                )}
              </Row>
            )}

            <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
              <Grid item xs={12} className="mb-3 ms-1 mt-1">
                {(!!resolveImages.length || !isAdmin) && (
                  <label className="fw-semibold h5">
                    <p className="mb-1">{t("Resolve Photographs")}:</p>
                  </label>
                )}
                <div className="d-flex gap-3 flex-wrap">
                  {!!resolveImages.length &&
                    resolveImages.map(
                      ({ photographUrl, url, name, id: currentId }, inx) => (
                        <div className="image-container" key={inx}>
                          <a
                            onClick={() => previewButtonHandler(photographUrl)}
                            target="_blank"
                          >
                            <Box
                              component="img"
                              className="image d-block m-auto"
                              alt="image"
                              src={url ?? getImageUrl(photographUrl)}
                            />
                          </a>
                          {!isAdmin && !isResolved && (
                            <i
                              className="ri-close-circle-line fs-3 close-btn"
                              onClick={() => {
                                setResolveImages(
                                  resolveImages.filter(
                                    ({ id }) => id !== currentId
                                  )
                                );
                              }}
                            />
                          )}
                          <Tooltip title={name}>
                            <DocumentName fontSize={13} variant="body2">
                              {name}
                            </DocumentName>
                          </Tooltip>
                        </div>
                      )
                    )}

                  {!isAdmin && !isResolved && (
                    <Box component="label" className="m-0 p-0 cursor-pointer">
                      <UploadImage>
                        <AddIcon
                          sx={{
                            color: "black",
                          }}
                        />
                      </UploadImage>
                      <input
                        type="file"
                        accept="image pdf/*"
                        onChange={(e) => {
                          fileChangeHandler(Array.from(e.target.files));
                          e.target.value = null;
                        }}
                        multiple
                        hidden
                      />
                    </Box>
                  )}
                </div>
                {isPlanOfActionMoreThanSevenDays && !isAdmin && !isResolved && (
                  <Grid item xs={12} className="mt-3">
                    <InputFieldText
                      label={t("Submit Plan Of Action") + "*"}
                      value={submitPlanOfAction}
                      onChange={(e) => setSubmitPlanOfAction(e.target.value)}
                      placeholder="Enter Submit Plan Of Action"
                      multiline
                      rows={2}
                    />
                  </Grid>
                )}
                {!isAdmin && !isResolved && (
                  <div className="d-flex justify-content-end mt-2">
                    <Button
                      size="medium"
                      variant="contained"
                      type="submit"
                      onClick={handleUploadResolvePhotographs}
                      disabled={
                        isResolvePhotographLoading ||
                        !(
                          !!resolveImages.length &&
                          (!isPlanOfActionMoreThanSevenDays ||
                            !!submitPlanOfAction)
                        )
                      }
                    >
                      {t("Upload")}
                      {isResolvePhotographLoading && (
                        <CircularProgress size={20} />
                      )}
                    </Button>
                  </div>
                )}

                {isAuthorizedAdminRole &&
                  !isResolved &&
                  !!resolveImages?.length && (
                    <div className="d-flex justify-content-end mt-2">
                      <Button
                        size="medium"
                        variant="contained"
                        type="submit"
                        color="success"
                        onClick={handleApproveResolvePhotographs}
                        disabled={isCloseBuildingObservationLoading}
                      >
                        {t("Approve")}
                        {isCloseBuildingObservationLoading && (
                          <CircularProgress size={20} />
                        )}
                      </Button>
                    </div>
                  )}
              </Grid>
            </Grid>

            <>
              {((isAuthorizedAdminRole && isApproved) || !isAdmin) && (
                <>
                  <div
                    className="fs-4 fw-semibold mb-3 border-bottom"
                    onClick={handleDiscussionCollapse}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <div>{t("Developer Discussion")}</div>{" "}
                      <p>{openDiscussion ? <ExpandLess /> : <ExpandMore />}</p>
                    </Stack>
                  </div>
                  <Collapse in={openDiscussion} timeout="auto" unmountOnExit>
                    {!isResolved && (
                      <CommentInput
                        handleSubmit={addNewComment}
                        isFireSafety
                        inspectionObservationId={observationId}
                      />
                    )}

                    {getCommentsByObservationId(commentData, observationId)
                      .length ? (
                      getCommentsByObservationId(
                        commentData,
                        observationId
                      ).map(({ comment, createdAt, name }, index) => (
                        <CommentBox
                          comment={comment}
                          name={name}
                          createdAt={createdAt}
                          key={index}
                        />
                      ))
                    ) : (
                      <Card variant="outlined" className="mt-2 p-0">
                        <CardContent className="pt-1">
                          <Typography color="GrayText" gutterBottom>
                            {t("No comment")}
                          </Typography>
                        </CardContent>
                      </Card>
                    )}
                  </Collapse>
                </>
              )}

              {isAdmin && (
                <AdminDiscussionForBuildingObservation
                  inspectionObservationId={observationId}
                  adminComments={getCommentsByObservationId(
                    adminComments,
                    observationId
                  )}
                  isResolved={isResolved}
                />
              )}
            </>
          </Row>
        </Collapse>
      </>
    </>
  );
};

FireSafetyViewBuildingObservationDetails.propTypes = {
  observationDetails: PropTypes.object,
  index: PropTypes.number,
  buildingInformationId: PropTypes.number,
  t: PropTypes.func,
};

export default withTranslation()(FireSafetyViewBuildingObservationDetails);
